import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/archive';
import {
  Breadcrumb,
  CategoryBadge,
  getArticle,
  getCategory,
  MetaLine,
  PostCardVertical,
} from '@/components/feature/PostCard';
import { formatViews, tone } from '@/utils/chigua';

const roleMap: Record<string, 'primary' | 'accent' | 'secondary'> = {
  tafang: 'primary',
  lianqing: 'accent',
  juzu: 'secondary',
  zongyi: 'secondary',
  hongtan: 'accent',
};

function BlockRenderer({ block }: { block: { t: string; d: string | string[] } }) {
  let node: ReactNode;
  if (block.t === 'h') {
    node = <h2 className="mb-3 mt-8 border-l-4 border-primary-500 pl-3 text-xl font-bold text-foreground-950 md:text-2xl">{block.d}</h2>;
  } else if (block.t === 'quote') {
    node = (
      <blockquote className="my-6 rounded-r-2xl border-l-4 border-accent-400 bg-accent-50/70 px-5 py-4 text-base font-medium leading-relaxed text-accent-900">
        {block.d}
      </blockquote>
    );
  } else if (block.t === 'list' && Array.isArray(block.d)) {
    node = (
      <ul className="my-4 space-y-2.5">
        {block.d.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-foreground-800">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  } else {
    node = <p className="my-4 text-[15px] leading-[1.9] text-foreground-800 md:text-base">{block.d}</p>;
  }
  return node;
}

export default function NewsDetailPage() {
  const { slug = '' } = useParams();
  const a = getArticle(slug);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (a) document.title = `${a.title} | 每日吃瓜网`;
  }, [a]);

  if (!a) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
        <i className="ri-error-warning-line text-5xl text-primary-500" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-3xl text-foreground-950">这口瓜好像下架了</h1>
        <p className="mt-2 text-foreground-500">你访问的文章不存在或已被删除，去看看别的热瓜吧。</p>
        <Link to="/" className="mt-6 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页吃瓜
        </Link>
      </div>
    );
  }

  const cat = getCategory(a.category);
  const role = cat ? tone[roleMap[a.category] ?? 'primary'] : tone.primary;
  const related = articles.filter((x) => x.slug !== slug).slice(0, 3);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  return (
    <article>
      <div className="border-b border-background-200/70 bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb
            items={[
              { name: '首页', to: '/' },
              { name: cat?.name ?? '专栏', to: `/column/${a.category}` },
              { name: '正文' },
            ]}
          />
          <div className="max-w-3xl">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <CategoryBadge slug={a.category} />
              <span className={`inline-flex items-center gap-1 text-xs font-medium ${role.text}`}>
                <i className="ri-eye-line" aria-hidden="true" /> {formatViews(a.views)} 围观
              </span>
            </div>
            <h1 className="font-heading text-2xl leading-tight text-foreground-950 md:text-4xl">{a.title}</h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">{a.summary}</p>
            <div className="mt-5">
              <MetaLine a={a} />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <figure className="overflow-hidden rounded-2xl">
              <img src={a.hero} alt={a.title} title={a.title} className="h-auto w-full object-cover object-top" />
            </figure>
            <div className="pt-4">
              {a.body.map((b, i) => (
                <BlockRenderer key={i} block={b} />
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm font-medium text-foreground-500">标签：</span>
              {a.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search/${encodeURIComponent(tag)}`}
                  className={`rounded-full ${role.soft} px-3 py-1 text-xs font-medium transition-colors hover:bg-foreground-900 hover:text-background-50`}
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-background-100 p-4">
              <p className="flex items-center gap-2 text-sm text-foreground-600">
                <i className="ri-share-forward-line text-primary-500" aria-hidden="true" /> 把这口瓜分享给朋友
              </p>
              <button
                type="button"
                onClick={copyLink}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-background-50 transition-colors ${
                  copied ? 'bg-secondary-500' : 'bg-primary-500 hover:bg-primary-600'
                }`}
              >
                <i className={copied ? 'ri-check-line' : 'ri-link'} aria-hidden="true" />
                {copied ? '已复制链接' : '复制链接'}
              </button>
            </div>
          </div>

          <aside className="space-y-5">
            <div className={`rounded-3xl ${role.soft} p-5`}>
              <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-quill-pen-line" aria-hidden="true" /> 编辑有话说
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                本瓜报内容基于公开线索整理，仅供娱乐参考。我们坚持不传谣、不造谣，真相以当事人与权威机构为准。
              </p>
              <Link to={`/column/${a.category}`} className="mt-3 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-foreground-800 hover:text-primary-600">
                回到栏目 <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
            <div className="rounded-3xl bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-thumb-up-line text-accent-500" aria-hidden="true" /> 相关热瓜
              </h3>
              <div className="mt-3 flex flex-col gap-4">
                {related.map((x) => (
                  <PostCardVertical key={x.slug} a={x} />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </article>
  );
}